import { ApolloError } from "@apollo/client";
import React, {
    ReactElement, useContext, useEffect,
    useMemo,
    useState,
} from "react";
import {
    AgentAssignmentPreference,
    Permission, Session,
    User,
    UserSoundSettings,
    useGetActiveSessionsQuery, useLogoutMutation,
    useSaveUserSettingsMutation,
    GetAccountDocument,
} from "../../__generated__/api";
import {
    cloneWithoutTypename,
    subscribeError, unsubscribeError,
} from "../../api";
import { Loading } from "../../components";
import { ErrorDefault } from "../../pages/error/Error";
import usePortal, { Entitlements } from "../PortalContext";
import useRouting from "../Routing";
import useLogging from "../logging/LoggingContext";

const REFRESH_INTERVAL = 1000 * 60 * 60;

const USER_KEY = "user";

export interface AuthenticationContext {
    isLoggedIn: boolean;
    userId?: string;
    user?: User & {email: string};
    userFlags: Record<string, string>;
    current?: Session;
    permissions: Set<Permission>;
    logout: () => void;
    invalidate: () => void;
    clearCache: () => void;
    saveUserFlag(key: string, value: string): void;
    sounds: UserSoundSettings
    saveSounds(sounds: UserSoundSettings): void;
    assignmentPreference: AgentAssignmentPreference;
    saveAssignmentPreference?: (preference: AgentAssignmentPreference) => void;
    entitlementIds: Entitlements;
}

interface CachedUser {
    isLoggedIn: boolean;
    session: Session;
    user: User & {email: string};
    userFlags: Record<string, string>;
    permissions: Permission[];
    lastUpdated: number;
}

export const AuthContext = React.createContext<AuthenticationContext>({
    isLoggedIn: false,
    userId: "",
    user: undefined,
    current: undefined,
    permissions: new Set(),
    userFlags: Object.create(null),
    assignmentPreference: AgentAssignmentPreference.Available,
    logout: () => {
        // logoutÍ
    },
    invalidate: () => {
        // logout
    },
    clearCache: () => {
        // logout
    },
    saveUserFlag: () => {
        // nothing
    },
    sounds: {},
    saveSounds: () => {
        // nothing
    },
    saveAssignmentPreference: () => {
        // nothing
    },
    entitlementIds: new Entitlements(),
});

export function AuthProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const { redirect, Paths } = useRouting();
    const { invalidate: invalidatePortal, entitlementIds } = usePortal();

    const {
        loading, data, error, refetch,
    } = useGetActiveSessionsQuery();
    const [logout] = useLogoutMutation();
    const [saveUserSettings] = useSaveUserSettingsMutation();

    const logging = useLogging();

    const [userFlags, setFlags] = useState<Record<string, string>>(() => data?.userSettings.userFlags ?? Object.create(null));
    const [sounds, setSounds] = useState<UserSoundSettings>(() => data?.userSettings.sounds ?? Object.create(null));
    const [assignmentPreference, setAssignmentPreference] = useState<AgentAssignmentPreference>(
        () => data?.userSettings.assignmentPreference ?? AgentAssignmentPreference.AutoSynchronize,
    );

    const session = data?.session;
    const user = session?.user;

    useEffect(() => {
        if (data?.userSettings.userFlags) {
            setFlags(data.userSettings.userFlags ?? Object.create(null));
        }
        if (data?.userSettings.sounds) {
            setSounds(data.userSettings.sounds ?? Object.create(null));
        }
        if (data?.userSettings.assignmentPreference) {
            setAssignmentPreference(data.userSettings.assignmentPreference ?? AgentAssignmentPreference.AutoSynchronize);
        }
    }, [data?.userSettings]);

    const clearCache = () => {
        localStorage.removeItem(USER_KEY);
    };

    const invalidate = () => {
        clearCache();
        refetch();
        invalidatePortal();
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            // ok. Do a local logout.
        }
        invalidate();
        invalidatePortal();
        redirect(Paths.DEFAULT);
    };

    useEffect(() => {
        const handler = () => {
            invalidate();
        };
        subscribeError(handler);
        return () => {
            unsubscribeError(handler);
        };
    }, []);

    const saveUserFlag = async (key: string, value: string) => {
        const newFlags = {
            ...userFlags,
            [key]: value,
        };
        setFlags(newFlags);
        await saveUserSettings({
            variables: {
                settings: {
                    userFlags: newFlags,
                },
            },
        });
    };

    const saveSounds = async (soundSettings: UserSoundSettings) => {
        setSounds(soundSettings);
        await saveUserSettings({
            variables: {
                settings: {
                    sounds: cloneWithoutTypename(soundSettings),
                },
            },
        });
    };

    const saveAssignmentPreference = async (preference: AgentAssignmentPreference) => {
        setAssignmentPreference(preference);
        await saveUserSettings({
            refetchQueries: [
                { query: GetAccountDocument },
            ],
            variables: {
                settings: {
                    assignmentPreference: preference,
                },
            },
        });
    };

    const isNotLoggedIn = (!loading && data?.session == null) || error?.message?.startsWith("Not authorized");
    const notLoggedIn = !isNotLoggedIn ? undefined : {
        isLoggedIn: false,
        permissions: new Set<Permission>(),
        userId: "",
        user: undefined,
        current: undefined,
        userFlags,
        sounds,
        assignmentPreference,
        logout: handleLogout,
        invalidate,
        clearCache,
        saveUserFlag,
        saveSounds,
        saveAssignmentPreference,
        entitlementIds,
    };

    const userCached = localStorage.getItem(USER_KEY);

    const state: AuthenticationContext | undefined = useMemo(() => {
        let current: AuthenticationContext | undefined;
        if (session && user) {
            current = {
                current: session as Session,
                user: user as User & {email: string},
                isLoggedIn: user != null,
                userId: user?.id,
                userFlags,
                assignmentPreference,
                permissions: new Set<Permission>(session?.permissions as Permission[] ?? []),
                logout: handleLogout,
                invalidate,
                clearCache,
                saveUserFlag,
                sounds,
                saveSounds,
                saveAssignmentPreference,
                entitlementIds,
            };
        }

        if (current) {
            localStorage.setItem(USER_KEY, JSON.stringify({
                isLoggedIn: current.isLoggedIn,
                lastUpdated: Date.now(),
                permissions: Array.from(current.permissions),
                session: current.current,
                user: current.user,
                userFlags: userFlags ?? current.userFlags ?? {},
                sounds,
            } as CachedUser));
        } else if (userCached) {
            const userRaw = JSON.parse(userCached) as CachedUser;
            if (Date.now() - (userRaw.lastUpdated ?? 0) < REFRESH_INTERVAL) {
                current = {
                    isLoggedIn: userRaw.isLoggedIn,
                    permissions: new Set(userRaw.permissions),
                    current: userRaw.session,
                    userFlags: userFlags ?? userRaw.userFlags ?? {},
                    user: userRaw.user,
                    userId: userRaw.user.id,
                    assignmentPreference,
                    logout: handleLogout,
                    invalidate,
                    clearCache,
                    saveUserFlag,
                    sounds,
                    saveSounds,
                    saveAssignmentPreference,
                    entitlementIds,
                };
            }
        }

        return current;
    }, [session, user, userCached != null, userFlags, sounds, assignmentPreference]);

    useEffect(() => {
        if (notLoggedIn) {
            logging.info({ }, "Started Unauthenticated session");
        } else if (state) {
            logging.info({ userId: state.userId }, "Started Authenticated Session");
        }
    }, [state?.user, notLoggedIn]);

    if (loading) {
        return <Loading />;
    }

    if (notLoggedIn) {
        return (
            <AuthContext.Provider value={notLoggedIn}>
                {children}
            </AuthContext.Provider>
        );
    }

    if (!state) {
        return <Loading />;
    }

    if (error) {
        return <ErrorDefault message={error instanceof ApolloError ? error.message : "Unknown error occurred."} />;
    }

    return (
        <AuthContext.Provider value={state}>
            {children}
        </AuthContext.Provider>
    );
}

export default function useCurrentSession(): AuthenticationContext {
    return useContext(AuthContext) ?? {};
}
